<script setup lang="ts">
import { ref } from 'vue'
import { 
  Calendar, 
  Hash, 
  DollarSign, 
  Activity, 
  Clock, 
  Stethoscope,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-vue-next'

interface BenefitType {
  id: number
  code: string
  code_description: string
}

interface PracticeType {
  code: string
  code_description: string
}

interface TariffCode {
  id: number
  benefit_type: BenefitType[]
  deleted: string | null
  date_created: string
  date_modified: string
  code: string
  code_description: string
  is_hbc: boolean
}

interface CodeType {
  code: string
  code_description: string
}

interface LimitType extends CodeType {
  id: number
}

interface FrequencyType extends CodeType {
  id: number
}

interface TariffRate {
  rate_id: string
  tariff_code: TariffCode
  practice_type: PracticeType[]
  billing_type: CodeType
  limit_type: LimitType
  frequency: FrequencyType
  deleted: string | null
  date_created: string
  date_modified: string
  applicable_year: number
  applicable_limits: number
  active: boolean
}

const tariffRates = ref<TariffRate[]>([
  {
    "rate_id": "f823441c-637d-4693-81f7-cea15d97b462",
    "tariff_code": {
      "id": 8,
      "benefit_type": [
        {
          "id": 4,
          "code": "RI",
          "code_description": "Remote Intensive"
        },
        {
          "id": 2,
          "code": "SI",
          "code_description": "Standard Intensive"
        }
      ],
      "deleted": null,
      "date_created": "2023-07-06T10:29:52.948688+02:00",
      "date_modified": "2024-01-29T02:46:36.386209+02:00",
      "code": "PCRT1",
      "code_description": "Palliative Care - Remote transition to Intensive - Remote GP",
      "is_hbc": false
    },
    "practice_type": [
      {
        "code": "006",
        "code_description": "Internal Practices"
      },
      {
        "code": "014",
        "code_description": "GP"
      },
      {
        "code": "015",
        "code_description": "General Practitioner"
      }
    ],
    "billing_type": {
      "code": "FFS",
      "code_description": "Fee for service"
    },
    "limit_type": {
      "id": 1,
      "code": "LT",
      "code_description": "Per Lifetime"
    },
    "frequency": {
      "id": 1,
      "code": "C",
      "code_description": "Per consultation"
    },
    "deleted": null,
    "date_created": "2024-12-15T16:46:26.641540+02:00",
    "date_modified": "2024-12-15T16:46:26.641561+02:00",
    "applicable_year": 2025,
    "applicable_limits": 1,
    "active": false
  },
  {
    "rate_id": "2cb3bdcd-729e-4a8c-8ffd-dfb807b4d97b",
    "tariff_code": {
      "id": 18,
      "benefit_type": [
        {
          "id": 4,
          "code": "RI",
          "code_description": "Remote Intensive"
        },
        {
          "id": 2,
          "code": "SI",
          "code_description": "Standard Intensive"
        }
      ],
      "deleted": null,
      "date_created": "2023-07-06T15:53:39.232670+02:00",
      "date_modified": "2024-01-29T02:36:42.933101+02:00",
      "code": "PCIH88",
      "code_description": "Nurse In Hospital Visit - Remote Intensive Tier 2 - Non-Consortium MDT",
      "is_hbc": false
    },
    "practice_type": [
      {
        "code": "006",
        "code_description": "Internal Practices"
      },
      {
        "code": "050",
        "code_description": "Group Practice"
      },
      {
        "code": "079",
        "code_description": "Hospice"
      },
      {
        "code": "080",
        "code_description": "Nursing Agencies"
      },
      {
        "code": "088",
        "code_description": "Registered Nurse"
      }
    ],
    "billing_type": {
      "code": "FFS",
      "code_description": "Fee for service"
    },
    "limit_type": {
      "id": 1,
      "code": "LT",
      "code_description": "Per Lifetime"
    },
    "frequency": {
      "id": 1,
      "code": "C",
      "code_description": "Per consultation"
    },
    "deleted": null,
    "date_created": "2024-12-15T16:20:54.173791+02:00",
    "date_modified": "2024-12-15T16:20:54.173803+02:00",
    "applicable_year": 2025,
    "applicable_limits": 32,
    "active": false
  }
])

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const getStatusColor = (active: boolean) => {
  return active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
}

const getStatusIcon = (active: boolean) => {
  return active ? CheckCircle : XCircle
}
</script>

<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-4xl font-bold text-slate-800 mb-2">Tariff Rates Management</h1>
        <p class="text-slate-600">Comprehensive view of healthcare tariff codes and billing information</p>
      </div>

      <!-- Cards Grid -->
      <div class="grid grid-cols-1 gap-6">
        <div 
          v-for="rate in tariffRates" 
          :key="rate.rate_id"
          class="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-slate-200"
        >
          <!-- Card Header -->
          <div class="bg-gradient-to-r from-[#225f7a] to-[#2c7a96] p-6 text-white">
            <div class="flex items-start justify-between mb-4">
              <div class="flex-1">
                <div class="flex items-center gap-3 mb-2">
                  <Stethoscope class="w-6 h-6" />
                  <h2 class="text-2xl font-bold">{{ rate.tariff_code.code }}</h2>
                  <component 
                    :is="getStatusIcon(rate.active)"
                    class="w-5 h-5"
                  />
                </div>
                <p class="text-blue-100 text-sm leading-relaxed">
                  {{ rate.tariff_code.code_description }}
                </p>
              </div>
              <div :class="getStatusColor(rate.active)" class="px-4 py-2 rounded-full font-semibold text-sm">
                {{ rate.active ? 'Active' : 'Inactive' }}
              </div>
            </div>

            <!-- Quick Stats -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div class="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div class="flex items-center gap-2 mb-1">
                  <Calendar class="w-4 h-4" />
                  <span class="text-xs opacity-90">Year</span>
                </div>
                <p class="text-xl font-bold">{{ rate.applicable_year }}</p>
              </div>
              <div class="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div class="flex items-center gap-2 mb-1">
                  <Hash class="w-4 h-4" />
                  <span class="text-xs opacity-90">Limits</span>
                </div>
                <p class="text-xl font-bold">{{ rate.applicable_limits }}</p>
              </div>
              <div class="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div class="flex items-center gap-2 mb-1">
                  <Activity class="w-4 h-4" />
                  <span class="text-xs opacity-90">Practices</span>
                </div>
                <p class="text-xl font-bold">{{ rate.practice_type.length }}</p>
              </div>
              <div class="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div class="flex items-center gap-2 mb-1">
                  <DollarSign class="w-4 h-4" />
                  <span class="text-xs opacity-90">ID</span>
                </div>
                <p class="text-xs font-mono">{{ rate.tariff_code.id }}</p>
              </div>
            </div>
          </div>

          <!-- Card Body -->
          <div class="p-6">
            <div class="grid md:grid-cols-2 gap-6">
              <!-- Left Column -->
              <div class="space-y-6">
                <!-- Benefit Types -->
                <div>
                  <h3 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Activity class="w-4 h-4 text-[#225f7a]" />
                    Benefit Types
                  </h3>
                  <div class="flex flex-wrap gap-2">
                    <span 
                      v-for="benefit in rate.tariff_code.benefit_type" 
                      :key="benefit.id"
                      class="bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium border border-blue-200"
                    >
                      <span class="font-bold">{{ benefit.code }}</span> - {{ benefit.code_description }}
                    </span>
                  </div>
                </div>

                <!-- Billing & Frequency Info -->
                <div class="grid grid-cols-2 gap-4">
                  <div class="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div class="flex items-center gap-2 mb-2">
                      <DollarSign class="w-4 h-4 text-[#225f7a]" />
                      <span class="text-xs font-semibold text-slate-600">Billing Type</span>
                    </div>
                    <p class="text-sm font-bold text-slate-800">{{ rate.billing_type.code }}</p>
                    <p class="text-xs text-slate-600">{{ rate.billing_type.code_description }}</p>
                  </div>

                  <div class="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div class="flex items-center gap-2 mb-2">
                      <Clock class="w-4 h-4 text-[#225f7a]" />
                      <span class="text-xs font-semibold text-slate-600">Frequency</span>
                    </div>
                    <p class="text-sm font-bold text-slate-800">{{ rate.frequency.code }}</p>
                    <p class="text-xs text-slate-600">{{ rate.frequency.code_description }}</p>
                  </div>
                </div>

                <!-- Limit Type -->
                <div class="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div class="flex items-center gap-2 mb-2">
                    <AlertCircle class="w-4 h-4 text-amber-600" />
                    <span class="text-xs font-semibold text-amber-900">Limit Type</span>
                  </div>
                  <p class="text-sm font-bold text-amber-900">{{ rate.limit_type.code }}</p>
                  <p class="text-xs text-amber-700">{{ rate.limit_type.code_description }}</p>
                </div>
              </div>

              <!-- Right Column - Practice Types -->
              <div>
                <h3 class="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Stethoscope class="w-4 h-4 text-[#225f7a]" />
                  Eligible Practice Types
                  <span class="ml-auto bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs">
                    {{ rate.practice_type.length }} total
                  </span>
                </h3>
                <div class="max-h-80 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                  <div 
                    v-for="practice in rate.practice_type" 
                    :key="practice.code"
                    class="bg-gradient-to-r from-slate-50 to-white p-3 rounded-lg border border-slate-200 hover:border-[#225f7a] hover:shadow-md transition-all"
                  >
                    <div class="flex items-start gap-3">
                      <div class="bg-[#225f7a] text-white px-2 py-1 rounded text-xs font-bold min-w-[3rem] text-center">
                        {{ practice.code }}
                      </div>
                      <span class="text-sm text-slate-700 flex-1">{{ practice.code_description }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer - Metadata -->
            <div class="mt-6 pt-6 border-t border-slate-200">
              <div class="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs text-slate-600">
                <div>
                  <span class="font-semibold">Rate ID:</span>
                  <p class="font-mono text-slate-800 mt-1 break-all">{{ rate.rate_id }}</p>
                </div>
                <div>
                  <span class="font-semibold">Created:</span>
                  <p class="text-slate-800 mt-1">{{ formatDate(rate.date_created) }}</p>
                </div>
                <div>
                  <span class="font-semibold">Modified:</span>
                  <p class="text-slate-800 mt-1">{{ formatDate(rate.date_modified) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #225f7a;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #2c7a96;
}
</style>